/* DataSourceBadge — Selo de transparência metodológica do Observatório Público.
 *
 * Exibe o tipo de fonte dos dados públicos exibidos:
 *   - real_collection      → verde (Coleta real)
 *   - internal_aggregation → azul-marinho (Base monitorada)
 *   - mock_demo            → dourado (Demonstração)
 *
 * Props:
 *   - mode: "real_collection" | "internal_aggregation" | "mock_demo"
 *   - label / shortLabel / note: opcionais (sobrescrevem defaults do mode)
 *   - variant: "default" | "minimal" | "pill" | "block"
 *   - withTooltip: boolean (default true)
 *   - className: string
 */
import { Database, Globe2, Sparkles, Info } from "lucide-react";

const PRESETS = {
  real_collection: {
    label: "Coleta real em fontes abertas",
    short: "Coleta real",
    note: "Dados obtidos por coleta automatizada em fontes abertas.",
    Icon: Globe2,
    classes: {
      pill: "bg-emerald-50 text-emerald-800 border-emerald-200 ring-emerald-500/10",
      dot: "bg-emerald-500",
      icon: "text-emerald-600",
    },
  },
  internal_aggregation: {
    label: "Dados agregados da base monitorada",
    short: "Base monitorada",
    note: "Os indicadores exibidos são calculados a partir da base interna monitorada pelo Projeto Nostradamus.",
    Icon: Database,
    classes: {
      pill: "bg-slate-50 text-[#0a2342] border-slate-200 ring-[#0a2342]/5",
      dot: "bg-[#0a2342]",
      icon: "text-[#0a2342]",
    },
  },
  mock_demo: {
    label: "Ambiente demonstrativo com dados simulados",
    short: "Demonstração",
    note: "Dados utilizados apenas para demonstração institucional do funcionamento do observatório.",
    Icon: Sparkles,
    classes: {
      pill: "bg-amber-50 text-amber-800 border-amber-200 ring-amber-500/10",
      dot: "bg-amber-500",
      icon: "text-amber-600",
    },
  },
};

const FALLBACK = PRESETS.internal_aggregation;

export default function DataSourceBadge({
  mode,
  label,
  shortLabel,
  note,
  variant = "default",
  withTooltip = true,
  className = "",
}) {
  const preset = PRESETS[mode] || FALLBACK;
  const finalLabel = label || preset.label;
  const finalShort = shortLabel || preset.short;
  const finalNote = note || preset.note;
  const { Icon } = preset;

  if (variant === "minimal") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] ${preset.classes.icon} ${className}`}
        title={withTooltip ? finalNote : undefined}
        data-testid={`data-source-badge-${mode || "internal_aggregation"}`}
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${preset.classes.dot}`} />
        {finalShort}
      </span>
    );
  }

  if (variant === "block") {
    return (
      <div
        className={`flex items-start gap-3 p-3.5 rounded-lg border ${preset.classes.pill} ring-1 ${className}`}
        data-testid={`data-source-badge-${mode || "internal_aggregation"}`}
      >
        <Icon size={16} className={`${preset.classes.icon} mt-0.5 shrink-0`} />
        <div className="min-w-0">
          <div className="font-display font-semibold text-[13px] leading-tight">{finalLabel}</div>
          <div className="text-[11.5px] opacity-80 leading-snug mt-0.5">{finalNote}</div>
        </div>
      </div>
    );
  }

  // default / pill
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10.5px] font-mono uppercase tracking-[0.18em] ring-1 ${preset.classes.pill} ${className}`}
      title={withTooltip ? finalNote : undefined}
      data-testid={`data-source-badge-${mode || "internal_aggregation"}`}
    >
      <span className={`relative flex h-1.5 w-1.5`}>
        <span className={`absolute inline-flex h-full w-full rounded-full ${preset.classes.dot} opacity-60 animate-ping`} />
        <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${preset.classes.dot}`} />
      </span>
      {finalShort}
      {withTooltip && <Info size={10} className="opacity-50" />}
    </span>
  );
}
